// The HEADLESS CHAT's wire protocol (ADR 0021 amendment of 2026-10-08): the messages an app's own chat UI
// (`createAppChat`, ./client.ts) and its host (`serveAppChat` of @cortex/app-sdk/chat/host) exchange
// by postMessage, and the shapes they carry.
//
//   app → host   { source: 'cortex-app-chat', id, op, args }
//   host → app   { source: 'cortex-app-chat-event', id, event }       (turn.start: every streamed event)
//   host → app   { source: 'cortex-app-chat-reply', id, ok, result | error }
import type { Block, ChatEvent } from './turn.ts';

export const APP_CHAT_REQUEST = 'cortex-app-chat';
export const APP_CHAT_REPLY = 'cortex-app-chat-reply';
export const APP_CHAT_EVENT = 'cortex-app-chat-event';
/** Bumped when a message changes shape; `hello` answers it. */
export const APP_CHAT_PROTOCOL = 1;

export const APP_CHAT_OPS = [
  'hello',
  'sessions.list',
  'sessions.get',
  'sessions.delete',
  'turn.start',
  'turn.stop',
  'reports.save',
  'reports.list',
  'reports.delete',
  'viz.render',
] as const;
export type AppChatOp = (typeof APP_CHAT_OPS)[number];

export interface AppChatRequest {
  source: typeof APP_CHAT_REQUEST;
  id: string;
  op: AppChatOp;
  args?: Record<string, unknown>;
}
export interface AppChatReply {
  source: typeof APP_CHAT_REPLY;
  id: string;
  ok: boolean;
  result?: unknown;
  error?: { message: string; status: number };
}
export interface AppChatEventMessage {
  source: typeof APP_CHAT_EVENT;
  id: string;
  event: ChatEvent;
}

/** What `hello` answers: whether the chat is there for this app, and what the host fixed. */
export interface AppChatHello {
  protocol: number;
  available: boolean;
  /** Why not, when it is not (the app's Chat companion is off, the person cannot reach the app). */
  reason?: string;
  project: string;
  app: string;
  /** The app declaration's title. */
  title?: string;
  /** 'app': the person reaches the project only through this app (an app user): read and answer only. */
  reach: 'project' | 'app';
  /**
   * Who draws the chat. Always 'app' from hosts of 2026-11-04 on: no host draws a chat window or speed dial over an
   * app (companion UI is off for every app); 'host' only from an older host.
   */
  chatUi: 'host' | 'app';
  /** The persona every turn runs as. */
  persona: 'analyst';
  /** The host draws a model-authored visualization (`viz` block) to SVG for the app (viz.render). */
  viz: boolean;
  /**
   * The host streams an answer's text as it is written when a turn asks for it (`turn.start` with
   * `stream: true`). Absent (an older host): the text comes at the end of the turn, as before.
   */
  stream?: boolean;
}

export interface AppChatSession {
  id: string;
  title: string;
  createdAt?: string;
  updatedAt?: string;
}
export interface AppChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  blocks: Block[];
  createdAt?: string;
}
export interface AppChatReport {
  id: string;
  title: string;
  createdAt: string;
  chartCount: number;
  /** The report's own page on the host (opens in a new tab, the person signed in). */
  url: string;
}
export interface AppChatTurnResult {
  status: 'ok' | 'refused' | 'error' | 'stopped';
  sessionId: string | null;
  text: string;
  blocks: Block[];
}
